import { History } from 'lucide-react'
import { formatChangeLogCreatedAt } from '@/lib/utils/formatChangeLogCreatedAt'
import applicationStyles from '@/features/application/styles/applicationList.module.css'
import styles from '../styles/emailTemplateCard.module.css'
import type { EmailTemplateNotesHistoryProps, ParsedChangeLine } from '../types'
import { formatEmailTemplateNoteChangeLines } from '../utils/formatEmailTemplateNoteChanges'

function parseChangeLine(line: string): ParsedChangeLine {
  const match = line.match(/^(.+?)\s*:\s*(.*?)\s*=>\s*(.*)$/)
  if (!match) {
    return { raw: line, field: '', from: '', to: '' }
  }
  return {
    raw: line,
    field: match[1].trim(),
    from: match[2].trim(),
    to: match[3].trim(),
  }
}

/** Strip HTML tags and collapse whitespace for readable history previews. */
function previewValue(value: string, max = 96): string {
  if (!value) return '—'
  const plain = value
    .replace(/<[^>]*>/g, ' ')
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/\s+/g, ' ')
    .trim()
  if (!plain) return '—'
  if (plain.length <= max) return plain
  return `${plain.slice(0, max).trimEnd()}…`
}

function ChangeDataCell({ lines }: { lines: string[] }) {
  if (lines.length === 0) {
    return <span>—</span>
  }

  return (
    <div className="space-y-1.5">
      {lines.map((line) => {
        const parsed = parseChangeLine(line)
        if (!parsed.field) {
          return (
            <div key={line} title={line}>
              {previewValue(line, 160)}
            </div>
          )
        }

        return (
          <div key={`${parsed.field}-${line.slice(0, 40)}`}>
            <span className="font-semibold text-[#111827]">{parsed.field}</span>
            <span className="text-[#6b7280]"> : </span>
            <span title={parsed.from || undefined}>{previewValue(parsed.from)}</span>
            <span className="text-[#6b7280]"> → </span>
            <span className="font-medium text-[#111827]" title={parsed.to || undefined}>
              {previewValue(parsed.to)}
            </span>
          </div>
        )
      })}
    </div>
  )
}

export function EmailTemplateNotesHistory({ notes }: EmailTemplateNotesHistoryProps) {
  if (!notes.length) return null

  return (
    <section
      className={styles.card}
      data-testid="email-template-notes-history"
      aria-labelledby="email-template-notes-history-heading"
    >
      <div className={styles.cardHeader}>
        <History className={styles.cardIcon} aria-hidden />
        <h3 id="email-template-notes-history-heading" className={styles.cardTitle}>
          Notes History
        </h3>
      </div>

      <div className="overflow-x-auto" data-testid="email-template-notes-history-table">
        <table className={applicationStyles.listingTable}>
          <thead>
            <tr>
              <th>Changes By</th>
              <th>Changes Reason</th>
              <th>Changes Data</th>
              <th>Created At</th>
            </tr>
          </thead>
          <tbody>
            {notes.map((note, index) => {
              const changeLines = formatEmailTemplateNoteChangeLines(note.changes_data)
              const createdLabel = note.created_at
                ? formatChangeLogCreatedAt(note.created_at, 'MM/dd/yyyy hh:mm a')
                : '—'

              return (
                <tr key={`${note.created_at ?? ''}-${index}`}>
                  <td className="whitespace-nowrap font-medium text-[#111827]">
                    {note.name || '—'}
                  </td>
                  <td className="max-w-[240px] break-words">
                    {note.change_reason || '—'}
                  </td>
                  <td className="min-w-[280px] max-w-[480px] align-top">
                    <ChangeDataCell lines={changeLines} />
                  </td>
                  <td className="whitespace-nowrap">
                    <time
                      dateTime={
                        note.created_at != null ? String(note.created_at) : undefined
                      }
                    >
                      {createdLabel}
                    </time>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </section>
  )
}
